import type { FetchResponse } from 'openapi-fetch';
import type { paths } from './coreApiSchema';
import { getItem, setItem } from '../utils/storage';
import { legacyLogoutRequest } from './legacySessions';
import type { SuccessResponse } from 'openapi-typescript-helpers';
import { API_VERSION } from '../config/app';
import { coreApiClient } from './apiClient';

/**
 * Rotates the session with the given refresh token and writes the new tokens
 * into the global storage slots. Intentionally does *not* touch per-tenant
 * storage or Redux: rotation callers (401 interceptors) sync the active
 * tenant's snapshot via `syncActiveTenantFromGlobalTokens`, while login /
 * add-organisation flows own per-tenant writes through `upsertTenantSession`.
 */
export const updateSession = async ({
  sessionId,
  refreshToken,
}: {
  sessionId: string;
  refreshToken: string;
}): Promise<
  FetchResponse<
    paths['/core/v1/sessions/{id}']['put'],
    SuccessResponse<paths['/core/v1/sessions/{id}']['put']['responses']>,
    'application/json'
  >
> => {
  const res = await coreApiClient.PUT('/core/v1/sessions/{id}', {
    params: {
      path: { id: sessionId },
      header: {
        authorization: `Bearer ${refreshToken}`,
        'cosafe-api-version': String(API_VERSION),
      },
    },
  });
  if (!res.error) {
    setItem('coreApiAccessToken', res.data.data.accessToken);
    setItem('coreApiRefreshToken', res.data.data.refreshToken);
    setItem('coreApiSessionId', res.data.data.id);
  }
  return res;
};

/** Deletes a specific server-side session. */
export const deleteSession = async (sessionId: string, accessToken: string): Promise<void> => {
  try {
    const res = await coreApiClient.DELETE('/core/v1/sessions/{id}', {
      params: {
        path: { id: sessionId },
        header: {
          authorization: `Bearer ${accessToken}`,
          'cosafe-api-version': String(API_VERSION),
        },
      },
    });
    if (res.error) {
      console.warn('deleteSession failed:', res.error);
    }
  } catch (e) {
    console.warn('deleteSession exception:', e);
  }
};

export const logoutRequest = async (): Promise<void> => {
  const sessionId = getItem('coreApiSessionId') as string | null;
  const accessToken = getItem('coreApiAccessToken') as string | null;

  // When ULF is enabled, we call Core API logout, otherwise, fall back to Legacy API logout.
  // Since both APIs share the same session database, calling one is sufficient.
  if (sessionId && accessToken) {
    await deleteSession(sessionId, accessToken);
    return;
  }

  try {
    await legacyLogoutRequest();
  } catch (e) {
    console.warn('Legacy logout failed:', e);
  }
};
